import React from 'react';
import { Box, Typography, Button, Paper, Grid, Divider, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SecurityIcon from '@mui/icons-material/Security';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PsychologyIcon from '@mui/icons-material/Psychology';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TableChartIcon from '@mui/icons-material/TableChart';
import LockOpenIcon from '@mui/icons-material/LockOpen';

export default function VipTeaser() {
  const theme = useTheme();
  const accentColor = '#ffca28'; // Złoty kolor akcentów

  // Używamy JSX w opisach, aby móc pogrubić kluczowe fragmenty
  const benefits = [
    {
      icon: <LockOpenIcon sx={{ color: accentColor, fontSize: 32 }} />,
      title: "Dostęp do Wszystkich Lig",
      desc: <>Odblokuj predykcje dla <strong>I, II, III Ligi</strong> (pakiet "Ligi Centralne") oraz wszystkich lig seniorskich w Polsce, <strong>aż po C Klasę!</strong> (pakiet "Ligi Regionalne")</>
    },
	{
      icon: <PsychologyIcon sx={{ color: accentColor, fontSize: 32 }} />,
      title: "Własne Scenariusze (spersonalizowany symulator)",
      desc: <>Edytuj wyniki przyszłych meczów i inne warunki. <strong>Sprawdzaj co się stanie</strong>, gdy Twoja drużyna przegra wszystkie mecze do końca sezonu, lub gdy od tej pory będzie grać jak Real Madryt.</>
    },
	{
      icon: <AssessmentIcon sx={{ color: accentColor, fontSize: 32 }} />,
      title: "Pełne dane predykcyjne",
      desc: <>Zobacz dokładne procentowe szanse na <strong>każde miejsce w tabeli</strong> dla każdej drużyny, <strong>progi punktowe, szczegółowe dane rankingowe</strong> i inne ciekawe statystyki.</>
    },
    {
      icon: <TrendingUpIcon sx={{ color: accentColor, fontSize: 32 }} />,
      title: "Historyczne Wykresy Szans i Rankingów",
      desc: <>Analizuj realną siłę drużyn na przestrzeni całego sezonu i <strong>wykrywaj trendy formy</strong> zanim zrobią to inni.</>
    },
    {
      icon: <SecurityIcon sx={{ color: accentColor, fontSize: 32 }} />,
      title: "Ligowe Wybryki i Anomalie",
      desc: <>Unikalne statystyki (np. "najbardziej wyrównane ligi", "mistrzowie meczów wyjazdowych") – <strong>wiedza niedostępna nigdzie indziej</strong>.</>
    },
    {
      icon: <TableChartIcon sx={{ color: accentColor, fontSize: 32 }} />,
      title: "Rozszerzone Tabele i Zaawansowane funkcje predykcyjne",
      desc: <>Dostęp do <strong>szczegółowych danych i modeli</strong>, wykraczających poza standardowe zestawienia.</>
    }
  ];

  // Style dla pogrubionych elementów (spójne z kolorem ikon)
  const boldStyle = { fontWeight: 'bold', color: accentColor };

  return (
    <Paper 
      elevation={12} 
      sx={{ 
        maxWidth: 850, // Nieco szerszy
        mx: 'auto', 
        mt: 8,
        mb: 8,
        borderRadius: 4,
        overflow: 'hidden',
        // Ciemne tło i jasny tekst
        background: '#1e1e1e',
        color: '#e0e0e0',
        border: '1px solid #333'
      }}
    >
      {/* Header Section - Ciemniejszy gradient */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #0a247a 0%, #1565c0 100%)', 
        p: 5, 
        textAlign: 'center',
        borderBottom: '1px solid #1e88e5'
      }}>
        <Typography variant="h4" fontWeight="900" gutterBottom sx={{ color: '#fff', letterSpacing: 1 }}>
          To są dane dla Ekspertów. <span style={{color: accentColor}}>Odblokuj pełen potencjał.</span>
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, color: '#b3e5fc' }}>
          Widzisz tylko wierzchołek góry lodowej. Zdobądź przewagę dzięki zaawansowanej analityce.
        </Typography>
      </Box>

      {/* Benefits Grid */}
      <Box sx={{ p: 5 }}>
        <Grid container spacing={4}>
          {benefits.map((b, index) => (
            <Grid item xs={12} sm={6} key={index}>
              {/* Flex-start zapewnia równe ułożenie ikony względem tekstu */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {/* Lekki margines górny dla optycznego wyrównania ikony z pierwszą linią tekstu */}
                <Box sx={{ mt: 0.5 }}>
                  {b.icon}
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="700" gutterBottom sx={{ color: '#fff' }}>
                    {b.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#b0b0b0', lineHeight: 1.6 }}>
                     {/* Renderowanie JSX w opisie i nakładanie styli na boldy */}
                     {React.Children.map(b.desc.props.children, child => {
                        if (child.type === 'strong') {
                            return <span style={boldStyle}>{child.props.children}</span>;
                        }
                        return child;
                      })}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 5, borderColor: '#444' }} />

        {/* Pricing/Action Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 4, fontWeight: 500, color: '#fff' }}>
            Przestań zgadywać. Zacznij analizować.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: 3 }}>
            <Button 
              variant="contained" 
              size="large" 
              component={RouterLink} 
              to="/kup-vip"
              sx={{ 
                px: 6, 
                py: 2, 
                fontSize: '1.2rem',
                fontWeight: '800',
                borderRadius: 3,
                backgroundColor: accentColor,
                color: '#000', // Czarny tekst na złotym przycisku dla kontrastu
                '&:hover': { backgroundColor: '#ffb300' },
                boxShadow: '0 4px 20px rgba(255, 202, 40, 0.4)' // Złota poświata
              }}
            >
              ZOSTAŃ EKSPERTEM (VIP)
            </Button>
            
            <Button 
              variant="outlined" 
              size="large" 
              component={RouterLink} 
              to="/login"
              sx={{ 
                px: 5, py: 2, fontWeight: '700', borderRadius: 3,
                borderColor: '#e0e0e0', color: '#e0e0e0',
                '&:hover': { borderColor: accentColor, color: accentColor }
              }}
            >
              Masz już konto? Zaloguj się
            </Button>
          </Box>
          
          <Typography variant="body2" sx={{ display: 'block', mt: 4, color: '#888' }}>
            Wszystkie podstawowe dane dla Ekstraklasy (bazowe predykcje, tabele) pozostają dostępne za darmo.
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}